import * as React from "react";
import { useRef, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";

export type FahrzeugscheinExtracted = Partial<{
  licensePlate: string;
  hsn: string;
  tsn: string;
  vin: string;
  manufacturer: string;
  typeName: string;
  driveType: string;
  powerKw: string;
  grossWeight: string;
  payload: string;
  firstRegistration: string;
  bodyType: string;
  vehicleClass: string;
}>;

const useStyles = makeStyles({
  wrap: {
    marginBottom: "20px",
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    width: "100%",
    backgroundColor: BRAND.pillarOrange,
    backgroundImage:
      "linear-gradient(135deg, #E66A2C 0%, #F38B4A 100%)",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: "16px",
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "20px",
    paddingRight: "20px",
    minHeight: "92px",
    textAlign: "center",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 6px 18px rgba(230,106,44,0.35)",
    transitionProperty: "transform, box-shadow, background-color",
    transitionDuration: "150ms",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 24px rgba(230,106,44,0.45)",
    },
    ":focus-visible": {
      outline: "3px solid rgba(255,255,255,0.6)",
      outlineOffset: "2px",
    },
  },
  tileActive: {
    backgroundColor: "#0E7C3A",
    backgroundImage: "linear-gradient(135deg, #0E7C3A 0%, #1FA655 100%)",
    boxShadow: "0 6px 18px rgba(14,124,58,0.35)",
  },
  tileError: {
    backgroundColor: "#9b1c1c",
    backgroundImage: "linear-gradient(135deg, #9b1c1c 0%, #c43b3b 100%)",
    boxShadow: "0 6px 18px rgba(155,28,28,0.35)",
  },
  icons: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    color: BRAND.white,
  },
  label: {
    fontSize: "1.15rem",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "0.01em",
  },
  hint: {
    display: "block",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.92)",
    marginTop: "3px",
  },
  fileName: {
    fontSize: "0.9rem",
    color: BRAND.white,
    marginLeft: "10px",
    fontWeight: 600,
    backgroundColor: "rgba(0,0,0,0.18)",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
  },
  hidden: {
    display: "none",
  },
});

const Svg = ({ children, size = 26 }: { children: React.ReactNode; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const DocumentIcon = () => (
  <Svg>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8" />
    <path d="M8 17h6" />
  </Svg>
);

const UploadIcon = () => (
  <Svg>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </Svg>
);

type OcrState = "idle" | "loading" | "success" | "error";

type Props = {
  /** Uploaded file (so the active state survives re-renders). */
  value?: File | null;
  /** Called whenever the user picks a file. Parent persists it. */
  onChange?: (file: File | null) => void;
  /** Fires after a successful OCR pass — parent should merge the extracted
   *  fields into its `answers` state and remember which keys were filled. */
  onExtracted?: (data: FahrzeugscheinExtracted, fileUrl: string) => void;
  /** Fires when OCR fails (Mistral unavailable, network, etc.). Parent can
   *  show a banner — the form must remain usable for manual entry. */
  onError?: (message: string) => void;
};

const FahrzeugscheinUploader: React.FC<Props> = ({
  value,
  onChange,
  onExtracted,
  onError,
}) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localFile, setLocalFile] = useState<File | null>(value ?? null);
  const [ocrState, setOcrState] = useState<OcrState>("idle");
  const file = value !== undefined ? value : localFile;

  const handleClick = () => inputRef.current?.click();

  const runOcr = async (f: File) => {
    setOcrState("loading");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(apiUrl("ocr/fahrzeugschein"), {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `OCR failed (${res.status})`);
      }
      const json = (await res.json()) as {
        url: string;
        extracted: FahrzeugscheinExtracted;
      };
      // Drop null values so callers can spread without overwriting with null.
      const cleaned: FahrzeugscheinExtracted = {};
      for (const [k, v] of Object.entries(json.extracted ?? {})) {
        if (v != null && v !== "") (cleaned as any)[k] = v;
      }
      onExtracted?.(cleaned, json.url);
      setOcrState("success");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      onError?.(message);
      setOcrState("error");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setLocalFile(f);
    onChange?.(f);
    if (f) void runOcr(f);
    else setOcrState("idle");
  };

  const tileStateClass =
    ocrState === "success" || (ocrState === "idle" && file)
      ? styles.tileActive
      : ocrState === "error"
        ? styles.tileError
        : undefined;

  const hintText =
    ocrState === "loading"
      ? "Fahrzeugschein wird gelesen…"
      : ocrState === "error"
        ? "Auslesen fehlgeschlagen — Felder bitte manuell ausfüllen."
        : ocrState === "success"
          ? "Daten übernommen — bitte prüfen."
          : "PDF oder Foto — wir lesen Fahrzeugdaten automatisch aus.";

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={mergeClasses(styles.tile, tileStateClass)}
        onClick={handleClick}
        aria-label="Fahrzeugschein hochladen"
        aria-busy={ocrState === "loading"}
      >
        <span className={styles.icons}>
          <DocumentIcon />
          <UploadIcon />
        </span>
        <span>
          <span className={styles.label}>Fahrzeugschein hochladen</span>
          <span className={styles.hint}>{hintText}</span>
        </span>
        {file && <span className={styles.fileName}>· {file.name}</span>}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className={styles.hidden}
        onChange={handleChange}
      />
    </div>
  );
};

export default FahrzeugscheinUploader;
